import { fleetRunPath } from '@/services/claude-fleet/claude-fleet.live'
import { continueContext } from '@/services/continue-in-claude-code'
import type { ContinueContext, ContinueKind } from '@/services/continue-in-claude-code'

import type { ClaudeItemLinkTarget, ClaudeItemPlace } from './claude-item.types'

export const JIRA_BROWSE_URL = 'https://tattle.atlassian.net/browse/'
const BITBUCKET = 'https://bitbucket.org/tattledevs'
const CONFLUENCE_PAGE_URL = 'https://tattle.atlassian.net/wiki/pages/viewpage.action?pageId='

/** An ADX path opens in the app; anything else opens in a new tab. */
export const isAppPath = (href: string): boolean => href.startsWith('/')

/** The place a URL opens in, from its host; another site is its host name, and anything but a web URL is empty. */
export function placeOf(href: string): ClaudeItemPlace | '' {
  if (isAppPath(href)) return 'ADX'
  const host = /^https?:\/\/([^/?#]+)/i.exec(href)?.[1]?.toLowerCase() ?? ''
  if (host === 'bitbucket.org') return 'Bitbucket'
  if (host.endsWith('.atlassian.net')) return href.includes('/wiki/') ? 'Confluence' : 'Jira'
  if (host === 'slack.com' || host.endsWith('.slack.com')) return 'Slack'
  if (host === 'console.aws.amazon.com' || host.endsWith('.console.aws.amazon.com')) return 'AWS'
  return host.replace(/^www\./, '')
}

export const openInLabel = (place: ClaudeItemPlace): string => `Open in ${place}`

/** `adx#12` as its repo and number. */
const repoNumber = (id: string): [string, string] => {
  const [repo = '', n = ''] = id.split('#')
  return [repo, n]
}

/**
 * Where one item lives outside ADX, in the order a row shows them. The item's key or title is its link to Claude's
 * page in ADX, so ADX is named here only for an item with no key to click (a run). A PR or build names its ticket's
 * Jira page too when `ticketKey` is given. Empty for an item with no page of its own (a test, a backmerge).
 */
export function itemLinks(item: ContinueContext | null, opts: { ticketKey?: string | null } = {}): ClaudeItemLinkTarget[] {
  if (!item) return []
  const { kind, id } = item
  const jira = (key: string): ClaudeItemLinkTarget => ({
    place: 'Jira',
    href: `${JIRA_BROWSE_URL}${encodeURIComponent(key)}`,
    ariaLabel: `Open ${key} in Jira`
  })
  const withTicket = (links: ClaudeItemLinkTarget[]) => (opts.ticketKey ? [...links, jira(opts.ticketKey)] : links)
  if (kind === 'ticket') return [jira(id)]
  if (kind === 'pr') {
    const [repo, n] = repoNumber(id)
    return withTicket([{ place: 'Bitbucket', href: `${BITBUCKET}/${repo}/pull-requests/${n}`, ariaLabel: `Open ${repo} #${n} in Bitbucket` }])
  }
  if (kind === 'build') {
    const [repo, n] = repoNumber(id)
    return withTicket([
      { place: 'Bitbucket', href: `${BITBUCKET}/${repo}/pipelines/results/${n}`, ariaLabel: `Open ${repo} build #${n} in Bitbucket` }
    ])
  }
  if (kind === 'run') return [{ place: 'ADX', href: fleetRunPath(id), ariaLabel: `Open run ${id} in ADX` }]
  if (kind === 'confluence') return [{ place: 'Confluence', href: `${CONFLUENCE_PAGE_URL}${encodeURIComponent(id)}` }]
  return []
}

/** A link the page already has, labelled by where it goes; null for anything but an ADX path or a web URL. */
export function linkTo(href: string, ariaLabel?: string): ClaudeItemLinkTarget | null {
  const place = placeOf(href)
  return place ? { place, href, ...(ariaLabel ? { ariaLabel } : {}) } : null
}

/** A link named for what it opens, so a row with several reads apart: "Open adx #12 in Bitbucket". */
export function namedLinkTo(href: string, name: string): ClaudeItemLinkTarget | null {
  const found = linkTo(href)
  return found && { ...found, ariaLabel: `Open ${name} in ${found.place}` }
}

/** `namedLinkTo` as a row's list of links, empty when there is no URL or it is not a web page. */
export function namedLinksTo(href: string | null | undefined, name: string): ClaudeItemLinkTarget[] {
  const found = href ? namedLinkTo(href, name) : null
  return found ? [found] : []
}

/** An item's own pages when it is one, else the URL the fleet sent, named for what it opens. */
export function itemOrSentLinks(item: ContinueContext | null, sentUrl: string | null | undefined, name: string): ClaudeItemLinkTarget[] {
  return item ? itemLinks(item) : namedLinksTo(sentUrl, name)
}

/** A PR's Bitbucket page (the PR, or its `adx#12` ref), and its ticket's Jira page when `ticketKey` is given. */
export function prLinks(pr: { repo: string; id: number | string } | string, opts: { ticketKey?: string | null } = {}): ClaudeItemLinkTarget[] {
  return itemLinks({ kind: 'pr', id: typeof pr === 'string' ? pr : `${pr.repo}#${pr.id}` }, opts)
}

/** A ticket's Jira page, named by its key; nothing for a missing or malformed key. */
export const ticketLinks = (key: string | null | undefined): ClaudeItemLinkTarget[] => itemLinks(continueContext('ticket', key))

/** One item's own page outside ADX, or '' for an item with none. */
export const itemPage = (kind: ContinueKind, id: string): string => itemLinks({ kind, id })[0]?.href ?? ''
